export const SECTION_NAME = "Return";

export const INTRO_TEXT = `A return transaction is to an RMA what an item
  receipt is to a PO. It is a record that you actually received the goods 
  from a customer.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "SO-12345",
    "date": "2019-05-08T09:13:00",
    "customer": {
      "id": 23,
      "name": "Test Customer"
    },
    "currency": {
      "id": 42,
      "name": "USD"
    },
    "location": {
      "id": 1,
      "name": "Warehouse"
    },
    "salesRep": null,
    "channel": {
      "id": 3,
      "name": "Catalog"
    },
    "department": null,
    "shippingMethod": {
      "id": 12,
      "name": "FedEx"
    },
    "trackingNumber": "FL8ES0FLSK930SFDANV8",
    "linkedTransaction": null,
    "exchangeRate": 1.00000000,
    "customerMessage": null,
    "comment": null,
    "customerNotes": "",
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Cust-Acct-No",
        "value": null,
        "dataType": "Number"
      }
    ],
    "shipBy": "",
    "shippingAmount": 0.00000,
    "total": 12,
    "archived": false,
    "summaryOnly": false,
    "hasSignature": false,
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "class": null,
        "job": null,
        "workcenter": null,
        "tax": {
          "taxable": false,
          "taxCode": null
        },
        "linkedTransaction": null,
        "description": null,
        "quantity": 1,
        "weight": 12,
        "volume": 12,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "unitprice": 12,
        "amount": 12,
        "altAmount": 0,
        "picked": 0,
        "shipped": 1,
        "invoiced": 1,
        "produced": 0,
        "returned": 0,
        "cost": 1,
        "margin": 1100,
        "listprice": 0,
        "percentdiscount": 0,
        "duedate": "2019-05-08T00:00:00",
        "uom": {
          "id": 2,
          "name": "doz"
        },
        "bin": null,
        "lot": null,
        "serials": null
      }
    ]
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a return, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "Customer for this transaction.",
  },
  {
    name: "currency",
    type: "reference",
    description: `Currency for this transaction.`,
  },
  {
    name: "location",
    type: "reference",
    description: `Location for this transaction.`,
  },
  {
    name: "salesRep",
    type: "reference",
    description: `Sales representative for this transaction.`,
  },
  {
    name: "channel",
    type: "reference",
    description: `Channel (e.g., Catalog, Retail Store) for this transaction.`,
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`,
  },
  {
    name: "shippingMethod",
    type: "reference",
    description: `Shipping method for this return.`,
  },
  {
    name: "trackingNumber",
    type: "string",
    description: `The carrier's tracking number for this return.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "exchangeRate",
    type: "decimal",
    description: `The exchange rate used for this transaction, if multicurrency is enabled.`,
  },
  {
    name: "customerMessage",
    type: "string",
    description: `Customer message field.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "The company’s internal comment about this transaction. This comment is not visible to the customer.",
  },
  {
    name: "customerNotes",
    type: "string",
    description: `Field for internal notes about customer.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "shipBy",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Date by which this order should be shipped.`,
  },
  {
    name: "shippingAmount",
    type: "decimal",
    description: `Shipping amount for this transaction.`,
  },
  {
    name: "total",
    type: "decimal",
    description: `Transaction total.`,
    readOnly: true,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `Indicates if the summary parameter was set when retrieving back this record.`,
    readOnly: true,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
    readOnly: true,
  },
  {
    name: "lines",
    type: "array of return line items",
    description: `The lines for this return.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this return line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on this return transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "tax",
    type: "tax information object",
    description: `The tax information for this line, if enabled.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity for this line item.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "unitPrice",
    type: "decimal",
    description: `The unit price for this item.  Must be above the item’s minimum price, if set.`,
  },
  {
    name: "amount",
    type: "decimal",
    description: `The sales amount for this line item. The amount must equal the quantity multiplied by the unit price.`,
  },
  {
    name: "altAmount",
    type: "",
    description: "Unused.",
  },
  {
    name: "picked",
    type: "decimal",
    description: `The number of items picked on this line.`,
  },
  {
    name: "shipped",
    type: "decimal",
    description: `The number of items shipped on this line.`,
  },
  {
    name: "invoiced",
    type: "decimal",
    description: `the number of items invoiced on this line.`,
  },
  {
    name: "produced",
    type: "",
    description: "Unused.",
  },
  {
    name: "returned",
    type: "",
    description: "Unused.",
  },
  {
    name: "cost",
    type: "decimal",
    description: `The cost for this line.`,
  },
  {
    name: "margin",
    type: "decimal",
    description: `The margin for this line.`,
  },
  {
    name: "listPrice",
    type: "decimal",
    description: `The list price for this item.`,
  },
  {
    name: "percentDiscount",
    type: "decimal",
    description: `The percentage discount applied to this line.`,
  },
  {
    name: "dueDate",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `The due date for this line.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`,
  },
  {
    name: "bin",
    type: "reference",
    description: `Bin location where returned item will be stored.`,
  },
  {
    name: "lot",
    type: "reference",
    description: `Returned item lot.`,
  },
  {
    name: "serials",
    type: "array of references",
    description: `Returned item serial number(s).`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the estimate will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, comment, or customer name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
  {
    name: "location",
    type: "string",
    description: `Filters transactions by location name.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/return?start=101&maxresults=100&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&location=warehouse&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
